import { useQuery } from "@tanstack/react-query";
import { Dialog } from "primereact/dialog";
import { requests } from "../../../../services/http-common";
import { Dropdown } from "primereact/dropdown";
import { useEffect, useState } from "react";
import { LabelValue } from "../../../../components/LabelValue/LabelValue";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import styles from "./CreateJobModal.module.scss";
import { formatContractOptionLabel } from "./CreateJobModal.utils";

enum AvailableForJobCreation {
  Available = "AVAILABLE",
  Unavailable = "UNAVAILABLE",
}

interface CreateJobModalProps {
  show: boolean;
  set: (state: boolean) => void;
}

export function CreateJobModal({
  show,
  set,
}: CreateJobModalProps): JSX.Element | null {
  const navigate = useNavigate();
  const [selectedContract, setSelectedContract] = useState<{
    name: string;
    leadId: string;
  }>();

  const { data, isLoading } = useQuery({
    queryKey: [`contract-list`],
    queryFn: () => {
      return requests.get(
        `/contract?availableForJobCreation=${AvailableForJobCreation.Available}`
      );
    },
    retry: false,
    cacheTime: 0,
  });

  useEffect(() => {
    if (!show) {
      return;
    }

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [show]);

  const contractOptionTemplate = (option: { name: string }) => (
    <span className={styles.contractOption} title={option.name}>
      {option.name}
    </span>
  );

  const handleSelectClick = () => {
    navigate(`/sales/contract/create/job/${selectedContract?.leadId}`);
  };

  return (
    <Dialog
      visible={show}
      onHide={() => set(false)}
      resizable={false}
      draggable={false}
      header="Choose a contract"
      modal
      style={{ width: 632 }}
      contentStyle={{ overflow: "visible" }}
    >
      <LabelValue label="Contracts" bold>
        <Dropdown
          disabled={isLoading}
          placeholder="Select contract"
          options={data?.result.map((c: any) => ({
            name: formatContractOptionLabel(c),
            leadId: c.lead.id,
          }))}
          className={styles.contractDropdown}
          panelClassName={styles.contractDropdownPanel}
          itemTemplate={contractOptionTemplate}
          style={{ width: 472, maxWidth: "100%" }}
          panelStyle={{ width: 472, maxWidth: "calc(100vw - 32px)" }}
          optionLabel="name"
          filter
          filterBy="name"
          filterPlaceholder="Search contracts"
          showFilterClear
          resetFilterOnHide
          emptyFilterMessage="No contracts match your search"
          appendTo="self"
          onChange={(e) => {
            setSelectedContract(e.value);
          }}
          value={selectedContract}
        />
      </LabelValue>
      <div className={styles.buttons}>
        <Button
          label="Cancel"
          className="p-button-text"
          onClick={() => set(false)}
        />
        <Button
          label="Select"
          className="p-button-m"
          type="button"
          disabled={!selectedContract}
          onClick={handleSelectClick}
        />
      </div>
    </Dialog>
  );
}
